/* eslint-disable react/prop-types */
import React, { FC, useMemo, useEffect, useState } from 'react';
import cls from 'classnames';

import CircleWrapper from '@ott/circle-wrapper';
import ChevronRightIcon from '@ott/icon-chevron-right';
import Modal from '@ott/modal';
import Button from '@ott/button';
import { useLayoutContext } from '@ott/layout-context';

import { sendStatisticsRequest } from './helpers';
import useBannerData from './hooks/useBannerData';
import { Product } from './types';
import styles from './MarketingBanner.scss';

export type Props = {
  /** ID баннерного места */
  adUnitId: string;
  /**
   * Название вертикали, например avia
   * Требуется для запроса на получение данных баннера
   * */
  product: Product;
  /** Дополнительный класс для стилизации, может потребоваться для марджинов */
  className?: string;
  /** Квери параметры, которые могут пробрасываться в запрос на получение данных баннера */
  queryParams?: Record<string, string | number>;
};

const MarketingBanner: FC<Props> = (props) => {
  const { adUnitId, product, className, queryParams = {} }: Props = props;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const bannerData = useBannerData(adUnitId, product, queryParams);
  const { isDesktop, isMobile } = useLayoutContext();

  useEffect(() => {
    if (bannerData) {
      const { banner } = bannerData;

      if (banner?.statisticsUrls?.bannerShown && bannerData.type !== 'popup') {
        sendStatisticsRequest(banner.statisticsUrls.bannerShown);
      }
    }
  }, [bannerData]);

  useEffect(() => {
    if (bannerData) {
      const { popup } = bannerData;

      if (popup?.statisticsUrls?.popupShown && isPopupOpen) {
        sendStatisticsRequest(popup.statisticsUrls.popupShown);
      }
    }
  }, [bannerData, isPopupOpen]);

  const handleBannerClick = () => {
    if (bannerData) {
      const { banner: { statisticsUrls } = {}, popup } = bannerData;

      if (statisticsUrls?.bannerClicked) {
        sendStatisticsRequest(statisticsUrls.bannerClicked);
      }

      if (popup) {
        setIsPopupOpen(true);
      }
    }
  };

  const handlePopupButtonClick = () => {
    if (bannerData) {
      const { popup } = bannerData;

      if (popup?.statisticsUrls?.popupClicked) {
        sendStatisticsRequest(popup.statisticsUrls.popupClicked);
      }
      setIsPopupOpen(false);
    }
  };

  // @TODO если есть массив buttons, то пока берем только первую кнопку
  const firstButton = useMemo(() => {
    if (bannerData?.banner?.buttons) {
      return bannerData.banner.buttons[0];
    }

    return null;
  }, [bannerData]);

  const href = useMemo(() => {
    if (bannerData?.popup) {
      return undefined;
    }

    return firstButton?.clickUrl || bannerData?.banner?.clickUrl;
  }, [bannerData, firstButton]);

  const renderIcon = () => {
    if (!bannerData?.banner?.icon) {
      return null;
    }

    return (
      // @ts-ignore
      <CircleWrapper className={styles.iconWrapper}>
        <img width="24" src={bannerData.banner.icon} alt="icon" />
      </CircleWrapper>
    );
  };

  const renderAction = () => {
    if (!bannerData.banner || bannerData?.banner?.backgroundImage) {
      return null;
    }

    if (!firstButton) {
      return <ChevronRightIcon className={styles.arrow} fill={bannerData?.banner?.arrowColor} />;
    }

    return (
      <div
        className={styles.action}
        style={{
          color: firstButton.titleColor,
          backgroundColor: firstButton.fillColor,
          border: `1px solid ${firstButton.borderColor}`,
        }}
      >
        {firstButton.title}
      </div>
    );
  };

  const renderContent = () => (
    <>
      {renderIcon()}

      {(bannerData?.banner?.title || bannerData?.banner?.description) && (
        <div className={styles.content}>
          {bannerData.banner.title && (
            <span className={styles.title} style={{ color: bannerData.banner.titleColor || '' }}>
              {bannerData.banner.title}
            </span>
          )}
          {bannerData?.banner?.description && (
            <span style={{ color: bannerData.banner.descriptionColor || '' }}>
              {bannerData.banner.description}
            </span>
          )}
        </div>
      )}

      {isDesktop &&
        bannerData?.banner?.backgroundImage &&
        bannerData?.banner?.backgroundImageHighDensity && (
          <img
            width="100%"
            src={bannerData.banner.backgroundImage}
            srcSet={`${bannerData.banner.backgroundImage} 704w, ${bannerData.banner.backgroundImageHighDensity} 1408w`}
            sizes="704px, 1408px"
            alt="background"
          />
        )}

      {isMobile &&
        bannerData?.banner?.backgroundImageAdaptive &&
        bannerData?.banner?.backgroundImageAdaptiveHighDensity && (
          <img
            width="100%"
            src={bannerData.banner.backgroundImageAdaptive}
            srcSet={`${bannerData.banner.backgroundImageAdaptive} 343w, ${bannerData.banner.backgroundImageAdaptiveHighDensity} 686w`}
            sizes="343px, 686px"
            alt="mobile-background"
          />
        )}

      {renderAction()}
    </>
  );

  const renderModal = () => {
    if (!bannerData?.popup || !isPopupOpen) {
      return null;
    }

    const { popup } = bannerData;

    return (
      <Modal
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        contentOptions={{
          size: 'wide',
          pic: popup.image && (
            <div
              style={{
                background: `url(${popup.image}) no-repeat center`,
                backgroundSize: 'cover',
                height: 180,
              }}
            />
          ),
          title: popup.title || '',
          footerContentNode: popup.buttonText && (
            <Button
              withFullWidth={true}
              href={popup?.clickUrl}
              onClick={handlePopupButtonClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              {popup.buttonText}
            </Button>
          ),
        }}
      >
        {popup.description && (
          <div
            className={styles.popupDescription}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: popup.description }}
          />
        )}
      </Modal>
    );
  };

  // @TODO данный тип будет обработан в последующих версиях компонента
  if (bannerData?.type === 'popup' || !bannerData.banner) {
    return null;
  }

  return (
    <>
      <a
        className={cls(
          styles.banner,
          bannerData?.banner?.backgroundImage && styles.imageOverride,
          className
        )}
        style={{ backgroundColor: bannerData?.banner?.backgroundColor }}
        href={href}
        onClick={handleBannerClick}
        rel="noopener noreferrer"
        target="_blank"
        data-locator="marketing-banner"
      >
        {renderContent()}
      </a>

      {renderModal()}
    </>
  );
};

export default MarketingBanner;
